import { Components, inputAdornmentClasses, outlinedInputClasses, Theme } from '@mui/material';
import { iconButtonClasses } from '@mui/material/IconButton';
import { pickersOutlinedInputClasses, pickersSectionListClasses } from '@mui/x-date-pickers-pro';

import { getOutlinedInputClasses } from './helpers';

type MuiPickersOutlinedInputTheme = Components<Theme>['MuiPickersOutlinedInput'];

const getPickersOutlinedInputClasses = (
  colors: { main: string; light: string; dark: string; shadow: string },
  variant: 'default' | 'attention',
) => getOutlinedInputClasses(colors, pickersOutlinedInputClasses, variant);

export const pickersOutlinedInputTheme: MuiPickersOutlinedInputTheme = {
  styleOverrides: {
    root: ({ ownerState, theme }) => ({
      backgroundColor: theme.palette.common.white,
      minHeight: 'var(--field-height)',
      borderRadius: 'var(--field-border-radius)',
      gap: 4,
      padding: '0 8px',
      [`& .${inputAdornmentClasses.root}`]: {
        marginRight: 0,
      },
      [`.${pickersSectionListClasses.root}`]: {
        fontSize: 'var(--field-font-size)',
        fontWeight: 'var(--field-font-weight)',
        lineHeight: 'var(--field-line-height)',
        padding: '9px 0',
        height: 'var(--field-height)',
        paddingLeft: 4,
      },
      [`& .${pickersSectionListClasses.sectionContent}, & .${pickersSectionListClasses.section}`]: {
        fontSize: 'var(--field-font-size)',
        fontWeight: 'var(--field-font-weight)',
        lineHeight: 'var(--field-line-height)',
        color: ownerState.isFieldValueEmpty ? 'var(--field-placeholder-color)' : 'var(--field-foreground-color)',
      },
      [`&.${pickersOutlinedInputClasses.focused} .${pickersSectionListClasses.sectionContent}, & .${pickersSectionListClasses.section}`]:
        {
          color: ownerState.isFieldValueEmpty
            ? 'var(--field-placeholder-color)'
            : 'var(--field-focus-foreground-color)',
        },
      ...(ownerState.inputColor === 'primary' && {
        ...getPickersOutlinedInputClasses(theme.palette.primary, 'default'),
        // There is special case in DatePicker in MUI to force `borderColor: color.main` with this cascade selector. Here we override it.
        [`&.${outlinedInputClasses.focused}:not(.${outlinedInputClasses.error}) .${pickersOutlinedInputClasses.notchedOutline}`]:
          {
            borderColor: theme.palette.primary.dark,
          },
      }),

      [`&.${pickersOutlinedInputClasses.disabled}`]: {
        [`.${pickersSectionListClasses.sectionContent}`]: {
          color: 'var(--field-disabled-placeholder-color)',
        },
        [`.${pickersOutlinedInputClasses.sectionAfter}`]: {
          color: 'var(--field-disabled-foreground-color)',
        },
        backgroundColor: 'var(--field-disabled-background)',
      },
      [`& .${iconButtonClasses.root}`]: {
        color: 'var(--icon-secondary-color)',
        width: 24,
        height: 24,
        marginLeft: 0,
      },
      [`&.${pickersOutlinedInputClasses.disabled} .${iconButtonClasses.root}`]: {
        color: 'var(--field-disabled-placeholder-color)',
      },
      ...((ownerState.inputColor === 'error' || ownerState.hasFieldError) &&
        getPickersOutlinedInputClasses(theme.palette.error, 'attention')),
      ...(ownerState.inputColor === 'warning' && getPickersOutlinedInputClasses(theme.palette.warning, 'attention')),
    }),
    notchedOutline: ({ theme }) => ({
      borderWidth: 1,
      borderColor: theme.palette.grey[200],
      top: -10,
    }),
  },
};
